import { useEffect, useId, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Copy } from 'lucide-react';
import type { BrandProfileView } from '@mg/shared';
import { api } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { Button } from '../../../components/ui/Button';
import { useDialogs } from '../../../components/ui/DialogProvider';
import { useToast } from '../../../components/ui/toast';
import { BRAND_TOASTS } from '../../brand/brandCopy';
import { adoptBrandProfile } from '../../brand/queries';
import { saveErrorText } from './useBrandInformationForm';

export const LEGACY_PANEL_TITLE = 'Previous guidance (not yet sorted)';
export const LEGACY_PANEL_TEXT =
  "We couldn't place this part of your previous brand guidance into the new fields. The AI still uses it as additional guidance until you dismiss it.";
export const LEGACY_DISMISS_CONFIRM = {
  title: 'Dismiss previous guidance?',
  message: "The AI will stop using this text. Copy anything you still need into your brand's details first.",
  confirmLabel: 'Dismiss',
  cancelLabel: 'Cancel',
} as const;
const COPY_FAILED_TEXT = "Couldn't copy the text. Select it and copy it yourself.";
/** How long "Copied" shows. */
const COPIED_MS = 2_000;

/**
 * "Previous guidance (not yet sorted)" (spec:461, O1): the part of the old Brand Description, Brand
 * Voice and Company Voice the migration could not place, which the AI keeps reading as additional
 * guidance until it is dismissed. A disclosure, closed at first; inside, the text as it was, with Copy
 * for everyone and Dismiss for admins (it asks first, then saves, with the toast: O4).
 */
export function LegacyGuidancePanel({
  tenantId,
  text,
  canEdit,
  onDismissed,
}: {
  tenantId: string;
  text: string;
  canEdit: boolean;
  /** The view the dismissal answered with. */
  onDismissed: (next: BrandProfileView) => void;
}) {
  const bodyId = useId();
  const queryClient = useQueryClient();
  const { confirm } = useDialogs();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFailed(false);
      setCopied(true);
    } catch {
      setCopied(false);
      setCopyFailed(true);
    }
  };

  const dismiss = async () => {
    const ok = await confirm({ ...LEGACY_DISMISS_CONFIRM });
    if (!ok) return;
    setDismissing(true);
    setError(null);
    try {
      const next = await api.dismissLegacyGuidance(tenantId);
      adoptBrandProfile(queryClient, next);
      onDismissed(next);
      toast.show(BRAND_TOASTS.changesSaved);
    } catch (err) {
      setError(saveErrorText(err));
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div className="rounded-lg border border-line">
      <h4 className="text-sm font-semibold text-ink">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((was) => !was)}
          className={cn(
            'focus-ring flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-hover',
            open ? 'rounded-t-lg' : 'rounded-lg',
          )}
        >
          <span>{LEGACY_PANEL_TITLE}</span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-ink-muted transition-transform motion-reduce:transition-none', open && 'rotate-180')}
            aria-hidden
          />
        </button>
      </h4>
      <div id={bodyId} hidden={!open} className="space-y-3 border-t border-line-soft px-4 py-3">
        <p className="text-sm text-ink-muted">{LEGACY_PANEL_TEXT}</p>
        {/* Focusable, so the text scrolls from the keyboard too. */}
        <div
          role="group"
          tabIndex={0}
          aria-label={LEGACY_PANEL_TITLE}
          className="focus-ring max-h-[20rem] overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-line bg-page px-3 py-2 text-sm text-ink"
        >
          {text}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => void copy()}>
            <Copy className="h-4 w-4" aria-hidden />
            Copy
          </Button>
          {canEdit && (
            <Button type="button" variant="plain" size="sm" loading={dismissing} onClick={() => void dismiss()}>
              Dismiss
            </Button>
          )}
          <span role="status" className="text-xs text-ink-muted">
            {copied ? 'Copied' : ''}
          </span>
        </div>
        {copyFailed && (
          <p role="alert" className="text-xs text-red-600 dark:text-error-dark">
            {COPY_FAILED_TEXT}
          </p>
        )}
        {error && (
          <p role="alert" className="text-xs text-red-600 dark:text-error-dark">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
